import React, { useState, useEffect } from 'react';
import { ConfigProvider, Tag } from 'antd';
import { DeleteTipo, GetAllTipos } from '@services/Tipo/Index.jsx';
import { GetAllServicos } from '@services/Servico/Index.jsx';
import FilterView from '@components/UI/FilterView/Index.jsx';
import { CustomLoader } from '@components/UI/CustomLoader/Index.jsx';
import { EditOptionComp } from '@components/UI/EditOption/Index.jsx';
import { DeleteOptionComp } from '@components/UI/DeleteOption/Index.jsx';
import ptBR from 'antd/es/locale/pt_BR';
import { StyledTable } from '@styles/datatables/datatable-antd.js';
import TextBlock from '@components/UI/TextBlock/Index..jsx';
import { flgStatusOptions } from '@enums/FlgStatus/Index';
import customStylesSelect from '@styles/react-select';
import Select from 'react-select';

const GridTipo = ({ state, setData, openModal, setSelectedRow, onDelete }) => {
  const [filters, setFilters] = useState({
    codigo: '',
    ano: '',
    servico: '',
    tipo: '',
    flgStatus: '',
  });
  const filterLabels = {
    codigo: 'CÓDIGO',
    ano: 'ANO',
    servico: 'SERVIÇO',
    tipo: 'TIPO',
    flgStatus: 'STATUS',
  };
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [servicesMap, setServicesMap] = useState({});

  // Busca os dados da API
  const fetchData = async (currentPage, currentRowsPerPage, filters = {}) => {
    setLoading(true);
    try {
      const response = await GetAllTipos(currentPage, currentRowsPerPage, filters);
      setData(response.items, response.totalItems);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Busca todos os serviços para mapear os IDs aos nomes
  const fetchAllServices = async () => {
    try {
      const response = await GetAllServicos(page, rowsPerPage);
      const map = response.items.reduce((acc, service) => {
        acc[service.id] = service.servico;
        return acc;
      }, {});
      setServicesMap(map);
    } catch (error) {
      console.error('Erro ao buscar todos os serviços:', error);
    }
  };

  // Atualiza os dados quando a página ou o número de linhas por página muda
  useEffect(() => {
    fetchData(page, rowsPerPage, filters);
  }, [page, rowsPerPage, filters]);

  // Busca todos os serviços ao carregar o componente
  useEffect(() => {
    fetchAllServices();
  }, []);

  // Manipula a mudança de página
  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    if (newRowsPerPage) {
      setRowsPerPage(newRowsPerPage);
    }
  };

  // Colunas da tabela
  const columns = [
    {
      title: 'CÓDIGO',
      dataIndex: 'codigo',
      key: 'codigo',
      width: '15%',
      sorter: (a, b) => a.codigo.localeCompare(b.codigo), // Ordenação
    },
    {
      title: 'ANO',
      dataIndex: 'ano',
      key: 'ano',
      width: '10%',
      sorter: (a, b) => a.ano.localeCompare(b.ano), // Ordenação
    },
    {
      title: 'SERVIÇO',
      key: 'servico',
      width: '25%',
      render: (_, record) =>
        <TextBlock>
          {servicesMap[record.idTipoServico] || 'Não encontrado'}
        </TextBlock>,
      sorter: (a, b) => (servicesMap[a.idTipoServico] || '').localeCompare(servicesMap[b.idTipoServico] || ''), // Ordenação
    },
    {
      title: 'TIPO',
      dataIndex: 'tipo',
      key: 'tipo',
      width: '25%',
      sorter: (a, b) => a.tipo.localeCompare(b.tipo), // Ordenação
      render: (text, record) => (
        <TextBlock>
          {text}
        </TextBlock>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'flgStatus',
      key: 'flgStatus',
      width: '15%',
      render: (flgStatus) => (
        <Tag color={(flgStatus) === true ? 'green' : 'red'}>
          {(flgStatus) === true ? 'ATIVO' : 'INATIVO'}
        </Tag>
      ),
      sorter: (a, b) => a.flgStatus - b.flgStatus, // Ordenação
    },
    {
      title: 'AÇÕES',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <div className="d-flex justify-content-center gap-3">
          <EditOptionComp
            row={record}
            setSelectedRow={setSelectedRow}
            openModal={openModal}
          />
          <DeleteOptionComp
            row={record}
            deleteFunction={DeleteTipo}
            onDelete={onDelete}
            itemNameKey={"tipo"}
          />
        </div>
      ),
    },
  ];

  const customFieldComponents = {
    flgStatus: ({ value, onChange }) => (
      <Select
        value={flgStatusOptions.find(opt => opt.value === value)}
        onChange={option => onChange(option?.value ?? "")}
        placeholder={"Selecione um status"}
        options={flgStatusOptions}
        isClearable
        isSearchable={false}
        styles={customStylesSelect}
      />
    )
  };

  return (
    <div className='px-4'>

      <FilterView
        filters={filters}
        setFilters={setFilters}
        filterLabels={filterLabels}
        customFieldComponents={customFieldComponents}
      />

      <div className='mt-3 mb-3'>
        <h1> Lista de Tipos Registrados </h1>
      </div>

      <ConfigProvider locale={ptBR}> {/* Localização em português */}
        <StyledTable
          columns={columns}
          dataSource={state.data}
          rowKey={"id"} // Chave única para cada linha
          loading={{
            spinning: loading,
            indicator: <CustomLoader />, // Spinner personalizado
          }}
          pagination={{
            current: page,
            pageSize: rowsPerPage,
            total: state.totalRows, // Total de registros
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`, // Exibe o total de registros
          }}
          scroll={{ y: '20rem', x: 'max-content' }}
          locale={{
            emptyText: <h2 className='m-3'>Nenhum registro disponível para exibição.</h2>, // Mensagem quando não há dados
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default GridTipo;